import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, MapPin, CalendarDays } from 'lucide-react';
import { getOutboundCallBaseUrl } from '../../lib/apiConfig';
import { Card, PageTitle, PrimaryButton, SecondaryButton } from './ui';
import { ErrorNote } from './DemoCustomerAIPages';

/* Calendar — AI-booked meetings. Endpoints live on the voice server (3017);
   getOutboundCallBaseUrl resolves to the same :3017 base the outbound calls use. */
const API_BASE = getOutboundCallBaseUrl();
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DemoCalendar = () => {
  const [cursor, setCursor] = useState(() => new Date());
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);

  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const monthEnd = useMemo(() => endOfMonth(cursor), [cursor]);

  const loadMeetings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${API_BASE}/api/calendar/meetings?start=${monthStart.toISOString()}&end=${monthEnd.toISOString()}`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load meetings');
      setMeetings(data.meetings || []);
    } catch (e) {
      setError(e.message);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const grid = useMemo(() => {
    const firstWeekday = monthStart.getDay();
    const cells = [];
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - firstWeekday);
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [monthStart]);

  const meetingsForDay = useCallback(
    (day) => meetings.filter((m) => sameDay(new Date(m.start), day)).sort((a, b) => new Date(a.start) - new Date(b.start)),
    [meetings]
  );

  const openCreate = (day) => {
    const start = new Date(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + 30);
    setModal({ id: null, form: { title: '', description: '', location: '', start: toLocalInput(start), end: toLocalInput(end) } });
  };

  const openEdit = (m) => {
    setModal({
      id: m._id,
      form: {
        title: m.title || '',
        description: m.description || '',
        location: m.location || '',
        start: toLocalInput(new Date(m.start)),
        end: toLocalInput(new Date(m.end)),
      },
    });
  };

  const saveMeeting = async () => {
    const f = modal.form;
    if (!f.title.trim()) return setError('Title is required');
    const isEdit = !!modal.id;
    try {
      const res = await fetch(`${API_BASE}/api/calendar/meetings${isEdit ? `/${modal.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          title: f.title,
          description: f.description,
          location: f.location,
          start: new Date(f.start).toISOString(),
          end: new Date(f.end).toISOString(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Failed to ${isEdit ? 'update' : 'create'} meeting`);
      setModal(null);
      loadMeetings();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/calendar/meetings/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to delete');
      setModal(null);
      loadMeetings();
    } catch (e) {
      setError(e.message);
    }
  };

  const today = new Date();
  const setField = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  return (
    <div className="flex h-full flex-col">
      <PageTitle
        title="Calendar"
        subtitle="Meetings and appointments booked across your Customer AI."
        action={
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <button
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-50"
                aria-label="Previous month"
              >
                <ChevronLeft size={17} />
              </button>
              <button
                onClick={() => setCursor(new Date())}
                className="rounded-lg px-3 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                Today
              </button>
              <button
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-50"
                aria-label="Next month"
              >
                <ChevronRight size={17} />
              </button>
            </div>
            <PrimaryButton onClick={() => openCreate(today)}>
              <Plus size={16} /> New meeting
            </PrimaryButton>
          </div>
        }
      />

      <div className="mt-5 flex shrink-0 items-center gap-3">
        <h2 className="text-[18px] font-bold tracking-tight">
          {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </h2>
        {loading && <span className="text-[12.5px] text-slate-400">Loading…</span>}
      </div>

      {error && <div className="mt-3"><ErrorNote error={error} /></div>}

      <Card className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Weekday header */}
        <div className="grid shrink-0 grid-cols-7 border-b border-slate-100 text-[11.5px] font-semibold text-slate-400">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-2.5 text-center">{w}</div>
          ))}
        </div>

        {/* Month grid */}
        <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
          {grid.map((day, i) => {
            const inMonth = day.getMonth() === cursor.getMonth();
            const isToday = sameDay(day, today);
            const dayMeetings = meetingsForDay(day);
            return (
              <div
                key={i}
                onClick={() => openCreate(day)}
                className={`min-h-0 cursor-pointer border-b border-r border-slate-100 p-1.5 transition-colors hover:bg-slate-50/70 ${
                  inMonth ? '' : 'bg-slate-50/50'
                }`}
              >
                <div className="flex justify-end">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-medium ${
                      isToday ? 'bg-teal-600 text-white' : inMonth ? 'text-slate-600' : 'text-slate-300'
                    }`}
                  >
                    {day.getDate()}
                  </span>
                </div>
                <div className="mt-1 space-y-1 overflow-hidden">
                  {dayMeetings.slice(0, 3).map((m) => (
                    <div
                      key={m._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(m);
                      }}
                      title={`${m.title} — click to edit`}
                      className="truncate rounded-md bg-teal-50 px-1.5 py-0.5 text-[11px] font-medium leading-tight text-teal-700 hover:bg-teal-100"
                    >
                      {new Date(m.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} {m.title}
                    </div>
                  ))}
                  {dayMeetings.length > 3 && (
                    <div className="px-1.5 text-[10px] text-slate-400">+{dayMeetings.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <p className="mt-2 flex shrink-0 items-center gap-1.5 text-[11.5px] text-slate-400">
        <CalendarDays size={13} /> Click any day to add a meeting, or click a meeting to edit or delete it.
      </p>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setModal(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
              <h3 className="text-[16px] font-semibold">{modal.id ? 'Edit meeting' : 'New meeting'}</h3>
              <button onClick={() => setModal(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3 p-5">
              <input
                autoFocus
                type="text"
                placeholder="Title"
                value={modal.form.title}
                onChange={(e) => setField('title', e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
              />
              <div className="grid grid-cols-2 gap-3">
                <label className="text-[12px] font-medium text-slate-500">
                  Start
                  <input
                    type="datetime-local"
                    value={modal.form.start}
                    onChange={(e) => setField('start', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-2.5 py-2 text-[13.5px] outline-none focus:border-teal-500"
                  />
                </label>
                <label className="text-[12px] font-medium text-slate-500">
                  End
                  <input
                    type="datetime-local"
                    value={modal.form.end}
                    onChange={(e) => setField('end', e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-2.5 py-2 text-[13.5px] outline-none focus:border-teal-500"
                  />
                </label>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2.5">
                <MapPin size={15} className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Location (optional)"
                  value={modal.form.location}
                  onChange={(e) => setField('location', e.target.value)}
                  className="w-full text-[14px] outline-none"
                />
              </div>
              <textarea
                placeholder="Description (optional)"
                rows={3}
                value={modal.form.description}
                onChange={(e) => setField('description', e.target.value)}
                className="w-full resize-none rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
              />
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-5 py-3.5">
              <div>
                {modal.id && (
                  <button
                    onClick={() => deleteMeeting(modal.id)}
                    className="rounded-lg px-3.5 py-2 text-[13px] font-semibold text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
                <PrimaryButton onClick={saveMeeting}>Save</PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoCalendar;
